export const ENTRY_METRICS = ['fcp', 'catalog_ready', 'catalog_error', 'video_ready', 'video_error', 'hidden_before_catalog'] as const;
type EntryMetricName = typeof ENTRY_METRICS[number];
export type EntryMetric = {
  metric: EntryMetricName; duration_ms: number; visit_id: string;
  device: 'mobile' | 'desktop'; connection: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  entry_kind: 'navigation' | 'spa';
};

/** Public metrics contain no input, account, media URL, query or persistent ID. */
export function parseEntryMetric(value: unknown): EntryMetric | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!ENTRY_METRICS.includes(v.metric as EntryMetricName)
    || typeof v.duration_ms !== 'number' || !Number.isFinite(v.duration_ms) || v.duration_ms < 0 || v.duration_ms > 300_000
    || typeof v.visit_id !== 'string' || !/^[a-f0-9-]{36}$/i.test(v.visit_id)
    || !['mobile', 'desktop'].includes(String(v.device))
    || !['slow-2g', '2g', '3g', '4g', 'unknown'].includes(String(v.connection))
    || !['navigation', 'spa'].includes(String(v.entry_kind))) return null;
  return { metric: v.metric as EntryMetricName, duration_ms: Math.round(v.duration_ms), visit_id: v.visit_id,
    device: v.device as EntryMetric['device'], connection: v.connection as EntryMetric['connection'], entry_kind: v.entry_kind as EntryMetric['entry_kind'] };
}

let visit: { id: string; sent: Set<EntryMetricName>; start: number; kind: EntryMetric['entry_kind'] } | undefined;

export function beginKagemanePerformance() {
  const navigation = performance.getEntriesByType('navigation')[0];
  const direct = navigation && new URL(navigation.name).pathname === '/kagemane';
  visit = { id: crypto.randomUUID(), sent: new Set(), start: direct ? 0 : performance.now(), kind: direct ? 'navigation' : 'spa' };
  return Boolean(direct);
}

export async function reportKagemanePerformance(metric: EntryMetricName, duration?: number): Promise<void> {
  try {
    if (!visit || visit.sent.has(metric)) return;
    visit.sent.add(metric);
    const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
    const body = JSON.stringify({ metric, duration_ms: Math.round(duration ?? performance.now() - visit.start), visit_id: visit.id,
      device: window.innerWidth < 768 ? 'mobile' : 'desktop', entry_kind: visit.kind,
      connection: ['slow-2g', '2g', '3g', '4g'].includes(connection ?? '') ? connection : 'unknown' });
    if (navigator.sendBeacon?.('/api/entry-performance', new Blob([body], { type: 'application/json' }))) return;
    await fetch('/api/entry-performance', { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true, credentials: 'omit' });
  } catch { /* Measurement must never affect browsing or wait for auth. */ }
}
